import { memo, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Chart, PieController, ArcElement, Tooltip } from 'chart.js';
import { CooperativeBalanceSituationWidgetModel } from 'models';
import { toMoneyFormat } from 'utils';
import _map from 'lodash/map';

import { WidgetContainer } from 'components';
import Box from '@mui/material/Box';

import clsx from 'clsx';
import { useStyles } from './style';

interface Props {
  cooperativeBalances: CooperativeBalanceSituationWidgetModel;
}

const SituationWidget: React.FC<Props> = ({ cooperativeBalances }) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const history = useHistory();
  const classes = useStyles();

  const forwardToCooperativeBalance = () => {
    history.push('/cooperative-balance');
  };

  const options = [
    {
      data: cooperativeBalances.PaidBalancePercentage || 0,
      background: '#224060',
      label: `${toMoneyFormat(cooperativeBalances.PaidBalance || 0)} €`,
    },
    {
      data: cooperativeBalances.UnpaidBalancePercentage || 0,
      background: '#5B7B9D',
      label: `${toMoneyFormat(cooperativeBalances.UnpaidBalance || 0)} €`,
    },
  ];

  const zeroBorder = options.some((item) => item.data === 0);

  const renderChart = () => {
    let context: CanvasRenderingContext2D | null = null;

    if (canvas && canvas.current) {
      context = canvas.current.getContext('2d');
    }

    if (context) {
      Chart.register(PieController, ArcElement, Tooltip);

      new Chart(context, {
        type: 'pie',
        data: {
          datasets: [
            {
              // fill: true,
              backgroundColor: _map(options, (item) => item.background),
              data: _map(options, (item) => item.data),
              borderWidth: zeroBorder ? 0 : 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                title: () => '',
                label: (context) => {
                  const item = options[context.dataIndex];
                  return item ? item.label : '';
                },
              },
            },
          },
        },
      });
    }
  };

  useEffect(() => renderChart(), []);

  return (
    <WidgetContainer
      themeColor="darkBlue"
      title={'Näytä koko maksutilanne'}
      action={forwardToCooperativeBalance}
    >
      <Box padding="15px">
        <h3 className={classes.title}>Vastikkeiden maksutilanne</h3>
      </Box>
      <Box display="flex" alignItems="center" padding="15px" height="100%">
        <Box width="40%" sx={{ width: 165, height: 165 }}>
          <canvas ref={canvas}></canvas>
        </Box>
        <Box padding="10px 5px" flex={1}>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              <span className={classes.text}>Maksettu</span>
              <span className={clsx(classes.percents, classes.darkBlue)}>
                {cooperativeBalances.PaidBalancePercentage} {'%'}
              </span>
            </li>
            <li className={classes.listItem}>
              <span className={classes.text}>Maksamatta</span>
              <span className={clsx(classes.percents, classes.greyBlue)}>
                {cooperativeBalances.UnpaidBalancePercentage} {'%'}
              </span>
            </li>
          </ul>
        </Box>
      </Box>
    </WidgetContainer>
  );
};

export default memo(SituationWidget);
